import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { PRODUCTS } from '@/constants/products';

/**
 * ProductAutocomplete
 * Free-text input with product suggestions from the catalog.
 * The user can type anything; the list only suggests as they type.
 */
const ProductAutocomplete = ({ value, onChange, disabled, placeholder = 'Ej: Coca-Cola', testId }) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapperRef = useRef(null);

  const suggestions = useMemo(() => {
    if (!value || value.length < 1) return [];
    const q = value.toLowerCase();
    return PRODUCTS.filter((p) => p.toLowerCase().includes(q)).slice(0, 8);
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const select = (name) => {
    onChange(name);
    setOpen(false);
    setHighlighted(-1);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        data-testid={testId}
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlighted(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || suggestions.length === 0) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, suggestions.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter' && highlighted >= 0) { e.preventDefault(); select(suggestions[highlighted]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && !disabled && (
        <ul
          data-testid={testId ? `${testId}-suggestions` : undefined}
          className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          {suggestions.map((name, idx) => (
            <li
              key={name}
              onMouseDown={(e) => { e.preventDefault(); select(name); }}
              onMouseEnter={() => setHighlighted(idx)}
              className={`px-3 py-2 text-sm cursor-pointer ${
                idx === highlighted ? 'bg-emerald-50 text-emerald-900' : 'text-slate-800 hover:bg-slate-50'
              }`}
            >
              {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProductAutocomplete;
